/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback, useRef} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'

import {KTSVG, TaxType} from '../../../../helpers'
import {Select, InputPrice, Datepicker} from '../../../../components'

import {useDispatch, useSelector} from 'react-redux'
import {
  allCompanyLookup,
  projectLookup,
  bankCashAccountGetAll,
  basicLookup,
  staffGetAll,
} from '../../../../redux/slices/lookup.slice'
import {expenseinvoiceFastCreate} from '../../../../redux/slices/expenseinvoice.slice'
import {categoryGetAll} from '../../../../redux/slices/category.slice'
import {useMemoizedValidationSchema, useMemoizedFormValues} from '../../../../utils/performanceOptimizations'
import {AuthorizedApiBase} from '../../../../api/api-client-config'

const selectLookupData = (state) => ({
  companyLookup: state.lookup.companyLookup,
  staffLookup: state.lookup.staffLookup,
  projectLookupList: state.lookup.projectLookupList,
  accountLookupList: state.lookup.accountLookupList,
  ExpensePaymentStatus: state.lookup.ExpensePaymentStatus,
  InvoiceKind: state.lookup.InvoiceKind,
  category: state.category.category
})

class AnalyzeDocumentApiClient extends AuthorizedApiBase {
  async analyze(file) {
    const formData = new FormData()
    formData.append('file', file)
    const options = await this.transformOptions({method: 'POST', body: formData})
    const baseUrl = window.ENVVARS?.REACT_APP_SERVICE_URL ?? ''
    const response = await fetch(`${baseUrl}/api/v1/expenseinvoice/AnalyzeDocument`, options)
    if (response.status === 204) return null
    if (!response.ok) throw new Error('Belge analiz edilemedi.')
    return response.json()
  }
}

const analyzeDocumentClient = new AnalyzeDocumentApiClient()

const ExpensesFast = ({CompanyId}) => {
  const [tax, setTax] = useState(TaxType[0].id)
  const [analyzeStatus, setAnalyzeStatus] = useState(null) // null | 'loading' | 'success' | 'error'
  const [filePreview, setFilePreview] = useState(null)
  const [autoFilledFields, setAutoFilledFields] = useState(new Set())
  const filePreviewRef = useRef(null)
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = location.pathname.split('/')

  const {
    companyLookup,
    staffLookup,
    projectLookupList,
    accountLookupList,
    ExpensePaymentStatus,
    InvoiceKind,
    category
  } = useSelector(selectLookupData)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(allCompanyLookup())
    dispatch(staffGetAll())
    dispatch(projectLookup())
    dispatch(bankCashAccountGetAll())
    dispatch(basicLookup('ExpensePaymentStatus'))
    dispatch(categoryGetAll({SourceTypeId: 8}))
    dispatch(basicLookup('InvoiceKind'))
  }, [dispatch])

  useEffect(() => {
    return () => {
      if (filePreviewRef.current) URL.revokeObjectURL(filePreviewRef.current)
    }
  }, [])

  const validSchema = useMemoizedValidationSchema(() =>
    Yup.object().shape({
      Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      ProjectId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      InvoiceKindId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    }), [intl]
  )

  const [expensePaymentStatus, setExpensePaymentStatus] = useState()

  const formValues = useMemoizedFormValues({
    Name: '',
    ProjectId: null,
    CompanyId: CompanyId || null,
    EditDate: new Date(),
    DefermentDate: new Date(),
    SubTotalPrice: '',
    TotalTax: '',
    TotalPrice: 0,
    ExpensePaymentStatusId: null,
    FromAccountId: null,
    CheckoutName: '',
    PaymentDate: new Date(),
    ExpenseFile: null,
    RelatedStaffId: '',
    InvoiceNumber: '',
    CategoryId: null,
    InvoiceKindId: '',
  })

  const handleSubmit = useCallback(async (values, {setSubmitting, resetForm}) => {
    setSubmitting(true)
    dispatch(expenseinvoiceFastCreate(values))
    resetForm()
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  useEffect(() => {
    const subTotal =
      formik.values.TotalPrice / (1 + Number(TaxType.find((item) => item.id == tax).value) / 100)
    const totalTax = formik.values.TotalPrice - subTotal

    formik.setFieldValue('SubTotalPrice', Number(subTotal.toFixed(4)))
    formik.setFieldValue('TotalTax', Number(totalTax.toFixed(4)))
  }, [tax, formik.values.TotalPrice])

  const handleFileChange = useCallback(async (event) => {
    const file = event.currentTarget.files[0]
    if (!file) return

    if (filePreviewRef.current) {
      URL.revokeObjectURL(filePreviewRef.current)
      filePreviewRef.current = null
    }
    setFilePreview(null)
    setAnalyzeStatus(null)
    setAutoFilledFields(new Set())

    formik.setFieldValue('ExpenseFile', file)

    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file)
      filePreviewRef.current = url
      setFilePreview(url)
    }

    setAnalyzeStatus('loading')
    try {
      const result = await analyzeDocumentClient.analyze(file)

      if (!result) {
        setAnalyzeStatus(null)
        return
      }

      const filled = []

      if (result.invoiceNumber) {
        formik.setFieldValue('InvoiceNumber', result.invoiceNumber)
        filled.push('InvoiceNumber')
      }
      if (result.invoiceDate) {
        formik.setFieldValue('EditDate', new Date(result.invoiceDate))
        filled.push('EditDate')
      }
      if (result.vendorName && !formik.values.Name) {
        formik.setFieldValue('Name', result.vendorName)
        filled.push('Name')
      }

      if (result.totalTax != null && result.totalTax > 0) {
        const effectiveSubTotal =
          result.subTotal != null ? result.subTotal : (result.totalAmount || 0) - result.totalTax
        if (effectiveSubTotal > 0) {
          const computedRate = Math.round((result.totalTax / effectiveSubTotal) * 100)
          const matchedTax = TaxType.find((t) => t.value === computedRate)
          if (matchedTax) setTax(matchedTax.id)
        }
      }

      if (result.totalAmount) {
        formik.setFieldValue('TotalPrice', Number(result.totalAmount.toFixed(2)))
        filled.push('TotalPrice')
      }

      setAutoFilledFields(new Set(filled))
      setTimeout(() => setAutoFilledFields(new Set()), 2500)
      setAnalyzeStatus('success')
    } catch {
      setAnalyzeStatus('error')
    }
  }, [formik])

  const fieldClass = (fieldName, base = 'form-control form-control-solid mb-3 mb-lg-0') =>
    autoFilledFields.has(fieldName) ? `${base} border border-success` : base

  const wrapClass = (fieldName) =>
    autoFilledFields.has(fieldName) ? 'border border-success rounded' : ''

  return (
    <form className='form' onSubmit={formik.handleSubmit} noValidate>

      {/* File upload */}
      <div className='row mb-6'>
        <label className='col-lg-3 fw-bold fs-6 mb-2'>
          {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEFAST.FILE'})}
        </label>
        <div className='col-lg-6 fv-row fv-plugins-icon-container'>
          <label
            htmlFor='ExpenseFile'
            className={`btn btn-outline btn-outline-dashed btn-outline-dark btn-active-light-dark position-relative overflow-hidden${analyzeStatus === 'loading' ? ' disabled pe-none' : ''}`}
          >
            <input
              className='position-absolute z-index-2 opacity-0 w-1px h-1px'
              type='file'
              accept='image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.kmz,.kml,.ncz,.dwg'
              id='ExpenseFile'
              name='ExpenseFile'
              onChange={handleFileChange}
              disabled={analyzeStatus === 'loading'}
            />
            <KTSVG
              path='/images/icons/duotune/communication/com008.svg'
              className='svg-icon-3 position-relative'
            />
            {formik.values.ExpenseFile?.name
              ? formik.values.ExpenseFile.name
              : intl.formatMessage({id: 'EXPENSEINVOICE.CREATEFAST.FILE'})}
          </label>

          {filePreview && (
            <div className='mt-2'>
              <img
                src={filePreview}
                alt='Fatura önizleme'
                style={{maxHeight: 60, maxWidth: 120, borderRadius: 4, border: '1px solid #dee2e6', objectFit: 'cover'}}
              />
            </div>
          )}

          <div className='text-muted fs-7 mt-1'>Fatura analizi için görüntü veya PDF önerilir.</div>

          {analyzeStatus === 'loading' && (
            <div className='mt-2 d-flex align-items-center gap-2 text-muted'>
              <span className='spinner-border spinner-border-sm'></span>
              <span>Belge analiz ediliyor...</span>
            </div>
          )}
          {analyzeStatus === 'success' && (
            <div className='mt-2 d-flex align-items-center gap-2 text-success fw-bold'>
              <span>&#10003;</span>
              <span>Form alanları dolduruldu.</span>
            </div>
          )}
          {analyzeStatus === 'error' && (
            <div className='mt-2 text-danger'>Belge analiz edilemedi. Alanları manuel doldurunuz.</div>
          )}
        </div>
      </div>

      {/* Name - required */}
      <div className='row mb-6'>
        <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
          {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEFAST.NAME'})}
        </label>
        <div className='col-lg-6 fv-row fv-plugins-icon-container'>
          <input
            type='text'
            required
            className={fieldClass('Name')}
            autoComplete='off'
            disabled={formik.isSubmitting}
            {...formik.getFieldProps('Name')}
          />
          {formik.touched.Name && formik.errors.Name && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.Name}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Project - required */}
      <div className='row mb-6'>
        <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
          {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEFAST.PROJECT'})}
        </label>
        <div className='col-lg-6 fv-row fv-plugins-icon-container'>
          <Select
            data={projectLookupList}
            required
            {...formik.getFieldProps('ProjectId')}
            onChange={(item) => formik.setFieldValue('ProjectId', Number(item && item.value))}
            menuPortalTarget={CompanyId ? null : document.body}
          />
          {formik.touched.ProjectId && formik.errors.ProjectId && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.ProjectId}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* InvoiceKind - required */}
      <div className='row mb-6'>
        <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
          {intl.formatMessage({id: 'SALEINVOICE.CREATE.INVOICEKINDNAME'})}
        </label>
        <div className='col-lg-6 fv-row fv-plugins-icon-container'>
          <Select
            data={InvoiceKind}
            required
            {...formik.getFieldProps('InvoiceKindId')}
            onChange={(item) => formik.setFieldValue('InvoiceKindId', Number(item && item.value))}
            menuPortalTarget={CompanyId ? null : document.body}
          />
          {formik.touched.InvoiceKindId && formik.errors.InvoiceKindId && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.InvoiceKindId}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Invoice Number */}
      <div className='row mb-6'>
        <label className='col-lg-3 fw-bold fs-6 mb-2'>
          {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEFAST.INVOICENUMBER'})}
        </label>
        <div className='col-lg-6 fv-row fv-plugins-icon-container'>
          <input
            type='text'
            className={fieldClass('InvoiceNumber')}
            autoComplete='off'
            disabled={formik.isSubmitting}
            {...formik.getFieldProps('InvoiceNumber')}
          />
        </div>
      </div>

      {/* EditDate */}
      <div className='row mb-6'>
        <label className='col-lg-3 fw-bold fs-6 mb-2'>
          {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEFAST.EDITDATE'})}
        </label>
        <div className='col-lg-6 fv-row fv-plugins-icon-container'>
          <div className={wrapClass('EditDate')}>
            <Datepicker
              {...formik.getFieldProps('EditDate')}
              onChange={([date]) => formik.setFieldValue('EditDate', date)}
            />
          </div>
        </div>
      </div>

      {/* DefermentDate */}
      <div className='row mb-6'>
        <label className='col-lg-3 fw-bold fs-6 mb-2'>
          {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEFAST.DEFERMENTDATE'})}
        </label>
        <div className='col-lg-6 fv-row fv-plugins-icon-container'>
          <Datepicker
            {...formik.getFieldProps('DefermentDate')}
            onChange={([date]) => formik.setFieldValue('DefermentDate', date)}
          />
        </div>
      </div>

      {/* TotalPrice */}
      <div className='row mb-6'>
        <label className='col-lg-3 fw-bold fs-6 mb-2'>
          {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEFAST.TOTALPRICE'})}
        </label>
        <div className='col-lg-6 fv-row fv-plugins-icon-container'>
          <div className={wrapClass('TotalPrice')}>
            <InputPrice
              {...formik.getFieldProps('TotalPrice')}
              onChange={(value) => formik.setFieldValue('TotalPrice', Number(value))}
            />
          </div>
        </div>
      </div>

      {/* TotalTax */}
      <div className='row mb-6'>
        <label className='col-lg-3 fw-bold fs-6 mb-2'>
          {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEFAST.TOTALTAX'})}
        </label>
        <div className='col-lg-6 fv-row fv-plugins-icon-container'>
          <div className='input-group'>
            <input
              readOnly
              type='text'
              className='form-control form-control-solid mb-3 mb-lg-0 w-75 bg-light text-muted'
              style={{cursor: 'not-allowed'}}
              autoComplete='off'
              {...formik.getFieldProps('TotalTax')}
            />
            <Select
              data={TaxType}
              value={tax}
              onChange={(item) => setTax(Number(item.value))}
              menuPortalTarget={CompanyId ? null : document.body}
            />
          </div>
          <span className='text-muted fs-7 mt-1'>KDV tutarı otomatik hesaplanır.</span>
        </div>
      </div>

      {/* Company */}
      <div className='row mb-6'>
        <label className='col-lg-3 fw-bold fs-6 mb-2'>
          {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEFAST.COMPANY'})}
        </label>
        <div className='col-lg-6 fv-row fv-plugins-icon-container'>
          <Select
            data={companyLookup}
            {...formik.getFieldProps('CompanyId')}
            onChange={(item) => formik.setFieldValue('CompanyId', Number(item && item.value))}
            menuPortalTarget={CompanyId ? null : document.body}
            disabled={CompanyId}
          />
        </div>
      </div>

      {/* Category */}
      <div className='row mb-6'>
        <label className='col-lg-3 fw-bold fs-6 mb-2'>
          {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEFAST.CATEGORY'})}
        </label>
        <div className='col-lg-6 fv-row fv-plugins-icon-container'>
          <Select
            data={category}
            {...formik.getFieldProps('CategoryId')}
            onChange={(item) => formik.setFieldValue('CategoryId', Number(item && item.value))}
            menuPortalTarget={CompanyId ? null : document.body}
          />
        </div>
      </div>

      {/* ExpensePaymentStatus */}
      <div className='row mb-6'>
        <label className='col-lg-3 fw-bold fs-6 mb-2'>
          {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEFAST.EXPENSEPAYMENTSTATUS'})}
        </label>
        <div className='col-lg-6 fv-row fv-plugins-icon-container'>
          <Select
            data={ExpensePaymentStatus}
            {...formik.getFieldProps('ExpensePaymentStatusId')}
            onChange={(item) => {
              formik.setFieldValue('ExpensePaymentStatusId', Number(item && item.value))
              setExpensePaymentStatus(Number(item.value))
            }}
            menuPortalTarget={CompanyId ? null : document.body}
          />
        </div>
      </div>

      {expensePaymentStatus == '2' && (
        <>
          <div className='row mb-6'>
            <label className='col-lg-3 fw-bold fs-6 mb-2'>
              {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEFAST.ACCOUNT'})}
            </label>
            <div className='col-lg-6 fv-row fv-plugins-icon-container'>
              <Select
                data={accountLookupList}
                {...formik.getFieldProps('FromAccountId')}
                onChange={(item) => formik.setFieldValue('FromAccountId', Number(item && item.value))}
                menuPortalTarget={CompanyId ? null : document.body}
              />
            </div>
          </div>
          <div className='row mb-6'>
            <label className='col-lg-3 fw-bold fs-6 mb-2'>
              {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEFAST.CHECKOUTNAME'})}
            </label>
            <div className='col-lg-6 fv-row fv-plugins-icon-container'>
              <input
                type='text'
                className='form-control form-control-solid mb-3 mb-lg-0'
                autoComplete='off'
                disabled={formik.isSubmitting}
                {...formik.getFieldProps('CheckoutName')}
              />
            </div>
          </div>
          <div className='row mb-6'>
            <label className='col-lg-3 fw-bold fs-6 mb-2'>
              {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEFAST.PAYMENTDATE'})}
            </label>
            <div className='col-lg-6 fv-row fv-plugins-icon-container'>
              <Datepicker
                {...formik.getFieldProps('PaymentDate')}
                onChange={([date]) => formik.setFieldValue('PaymentDate', date)}
              />
            </div>
          </div>
        </>
      )}

      {/* Related Staff */}
      <div className='row mb-6'>
        <label className='col-lg-3 fw-bold fs-6 mb-2'>
          {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEFAST.RELATEDSTAFF'})}
        </label>
        <div className='col-lg-6 fv-row fv-plugins-icon-container'>
          <Select
            data={staffLookup}
            {...formik.getFieldProps('RelatedStaffId')}
            onChange={(item) => formik.setFieldValue('RelatedStaffId', Number(item && item.value))}
            menuPortalTarget={CompanyId ? null : document.body}
          />
        </div>
      </div>

      <div className='row pt-15'>
        <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
          {!CompanyId && (
            <button
              className='btn btn-light'
              onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}`)}
            >
              {intl.formatMessage({id: 'BUTTON.CANCEL'})}
            </button>
          )}
          <button
            type='submit'
            className='btn btn-primary'
            data-kt-users-modal-action='submit'
            disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
          >
            <span className='indicator-label'>{intl.formatMessage({id: 'BUTTON.SUBMIT'})}</span>
            {formik.isSubmitting && (
              <span className='indicator-progress'>
                {intl.formatMessage({id: 'PLEASE'})}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </div>
    </form>
  )
}

export {ExpensesFast}
